/**
 * Module dependencies.
 */

import { Button, Navigation, Richtext, Type } from 'src/components/core';
import { ReactElement } from 'react';
import { color, media, units } from '@untile/react-components';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useRouter } from 'next/router';
import Container from 'src/components/core/layout/container';
import Metatags from 'src/components/core/metatags';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  subtitle: string;
  text: string | null;
  title: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: ${color('backgrounds.taupe01')};
  min-height: 100vh;
  padding: ${theme('dimensions.navbarHeightMobile')}px 0 ${units(8)};
  position: relative;

  ${media.min('md')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(
    2
  )}) 0 ${units(18)};
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      'navigation'
      'title'
      'subtitle'
      'button'
      'content';
    grid-template-columns: 100%;
  `}

  ${media.min('md')`
    grid-column-gap: ${theme('grid.columnsGutter')}px;
    grid-template-areas:
      '. navigation .      .'
      '. title      .      .'
      '. subtitle   button .'
      '. content    .      .';
    grid-template-columns: 1fr 8fr 2fr 1fr;
  `}
`;

/**
 * `StyledNavigation` styled component.
 */

const StyledNavigation = styled(Navigation)`
  grid-area: navigation;

  ${media.min('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1).attrs({ as: 'h1' })`
  grid-area: title;

  ${media.min('md')`
    margin-bottom: ${units(1)};
  `}
`;

/**
 * `Subtitle` styled component.
 */

const Subtitle = styled(Type.H4).attrs({ as: 'h4' })`
  font-weight: 300;
  grid-area: subtitle;
  margin-bottom: ${units(3)};

  ${media.min('md')`
    margin-bottom: 0;
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  grid-area: button;

  ${media.min('md')`
    justify-self: end;
  `}
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  grid-area: content;
  padding-top: ${units(6)};

  ${media.min('md')`
    padding-top: ${units(9)};
  `}
`;

/**
 * `Legal` component.
 */

const Legal = (props: Props): ReactElement => {
  const { subtitle, text, title } = props;
  const router = useRouter();
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();

  return (
    <>
      <Metatags
        description={subtitle}
        pageTitle={title}
        title={title}
      />

      <Wrapper>
        <Container>
          <Grid>
            <StyledNavigation
              label={translate('common:actions.goBack')}
              onClick={() => router.back()}
            />

            <Title>{title}</Title>

            <Subtitle>{subtitle}</Subtitle>

            <StyledButton
              colorTheme={'secondary'}
              href={routeResolver('contactUs')}
            >
              {translate('common:actions.reachOutToUs')}
            </StyledButton>

            <ContentWrapper>
              <Richtext>{text}</Richtext>
            </ContentWrapper>
          </Grid>
        </Container>
      </Wrapper>
    </>
  );
};

/**
 * Export `Legal` component.
 */

export default Legal;
